// Counts down the AI messaging preview while it is actually visible, then briefly shows the
// sent state before looping. Keeping this client-side makes the marketing demo feel alive
// without creating server traffic for a decorative preview.
const DEFAULT_DURATION_SECONDS = 28;
const TICK_MS = 250;
const SENT_PAUSE_MS = 2200;

const AutosendTimerHooks = {
  MarketingAutosendTimer: {
    mounted() {
      this.duration = Number.parseInt(this.el.dataset.duration, 10) || DEFAULT_DURATION_SECONDS;
      this.remaining = this.duration;
      this.visible = false;
      this.phase = 'countdown';

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      this.observer = new IntersectionObserver(
        ([entry]) => {
          this.visible = entry.isIntersecting;
          this.apply();
        },
        { threshold: 0.5 },
      );
      this.observer.observe(this.el);

      this.onVisibilityChange = () => this.apply();
      document.addEventListener('visibilitychange', this.onVisibilityChange);
    },

    destroyed() {
      this.pauseCountdown();
      clearTimeout(this.resetTimer);
      this.observer?.disconnect();
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    },

    apply() {
      if (!this.visible || document.hidden) {
        this.pauseCountdown();
        clearTimeout(this.resetTimer);
        this.resetTimer = null;
        return;
      }

      if (this.phase === 'sent') {
        this.scheduleReset();
      } else {
        this.startCountdown();
      }
    },

    startCountdown() {
      if (this.tickTimer) return;

      this.deadline = Date.now() + this.remaining * 1000;
      this.tickTimer = setInterval(() => this.tick(), TICK_MS);
      this.tick();
    },

    pauseCountdown() {
      if (!this.tickTimer) return;

      clearInterval(this.tickTimer);
      this.tickTimer = null;
      this.remaining = Math.max(Math.ceil((this.deadline - Date.now()) / 1000), 0);
    },

    tick() {
      this.remaining = Math.max(Math.ceil((this.deadline - Date.now()) / 1000), 0);
      this.renderCountdown();

      if (this.remaining === 0) this.markSent();
    },

    markSent() {
      clearInterval(this.tickTimer);
      this.tickTimer = null;
      this.phase = 'sent';

      this.el.querySelector('[data-autosend-label]').textContent = 'AI draft · sent automatically';
      this.el.querySelector('[data-autosend-countdown]').hidden = true;
      this.el.querySelector('[data-autosend-action-label]').textContent = 'Sent automatically';
      this.scheduleReset();
    },

    scheduleReset() {
      if (this.resetTimer) return;

      this.resetTimer = setTimeout(() => {
        this.resetTimer = null;
        this.phase = 'countdown';
        this.remaining = this.duration;
        this.renderCountdown();
        this.el.querySelector('[data-autosend-action-label]').textContent = 'Approve & send';
        this.apply();
      }, SENT_PAUSE_MS);
    },

    renderCountdown() {
      this.el.querySelector('[data-autosend-label]').textContent = 'AI draft · ready to send';
      const countdown = this.el.querySelector('[data-autosend-countdown]');
      countdown.hidden = false;
      countdown.textContent = `${this.remaining}s`;
    },
  },
};

export default AutosendTimerHooks;
